
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import { Client, Message, over, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { AppSettings } from 'src/app/services/AppSettings';

export enum SocketClientStateStream {
  ATTEMPTING, CONNECTED, DISCONNECTED
}

interface ConnectionDetail {
  timestamp: Date;
  state: SocketClientStateStream;
  client?: Client;
}

@Injectable({
  providedIn: 'root'
})
export class SocketServiceStream implements OnDestroy {

  private client: Client;
  private state: BehaviorSubject<SocketClientStateStream>;
  private subscribedTopics: Set<string>;
  // One entry per live onMessage() observer: re-attaches that observer's STOMP
  // callback to a freshly connected client. reSubscribeTopics() used to re-subscribe
  // with the bare jsonHandler, which parsed each frame and handed it to nobody, so
  // after the first reconnect the topic looked subscribed while StreamService never
  // received another frame (and its "already subscribed" guard refused to re-arm).
  private topicResubscribers = new Map<string, (client: Client) => void>();
  private reconnectInterval: any;
  private connectionList: ConnectionDetail[] = [];
  private activeClient: Client | null = null;

  constructor() {
    this.state = new BehaviorSubject<SocketClientStateStream>(SocketClientStateStream.ATTEMPTING);
    this.subscribedTopics = new Set<string>();
    // this.setupNetworkListeners();
    this.disconnectAllClients();
    this.initiateConnection();
  }

  private setupNetworkListeners() {
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
    console.log('Network listeners set up.');
  }

  private handleOnline() {
    if (!this.client.connected && !this.reconnectInterval) {
      setTimeout(() => this.reconnect(), 2000); // Add a delay to prevent spamming
    }
  }


  private handleOffline() {
    console.log('Network disconnected.');
    this.state.next(SocketClientStateStream.DISCONNECTED);
  }

  public initiateConnection() {
    this.client = over(() => new SockJS(AppSettings.webSocketUrlStream));
    // this.client.debug = (msg: string) => console.log(new Date(), msg);
    this.client.connect({}, () => {
      console.log('WebSocket connection established');
      this.state.next(SocketClientStateStream.CONNECTED);
      this.addConnectionDetail(SocketClientStateStream.CONNECTED, this.client);
      this.activeClient = this.client;
      this.reSubscribeTopics();
    });
    this.client.reconnect_delay = 10000;
    this.client.heartbeat.incoming = 4000;
    this.client.heartbeat.outgoing = 4000;
  }

  public reconnect() {
    if (this.state.getValue() !== SocketClientStateStream.CONNECTED) {
      if (!this.reconnectInterval) {
        this.reconnectInterval = setInterval(() => {
          if (this.client && !this.client.connected) {
            console.log('Attempting to reconnect...');
            this.disconnectClient();
            this.initiateConnection();
          } else {
            console.log('Reconnection successful. Clearing interval...');
            clearInterval(this.reconnectInterval);
            this.reconnectInterval = undefined;
          }
        }, 5000);
      }
    }
  }



  public disconnectClient() {
    if (this.client && this.client.connected) {
      this.client.disconnect(() => {
        console.log('WebSocket connection deactivated');
        this.state.next(SocketClientStateStream.DISCONNECTED);
        this.addConnectionDetail(SocketClientStateStream.DISCONNECTED, this.client);
      });
    } else {
      if (this.client.ws) {
        console.log('Force closing WebSocket connection...');
        this.client.ws.onclose = () => {}; // Disable onclose handler temporarily
        this.client.ws.close();
        this.state.next(SocketClientStateStream.DISCONNECTED);
        this.addConnectionDetail(SocketClientStateStream.DISCONNECTED, this.client);
      }
    }
    clearInterval(this.reconnectInterval);
  }

  public disconnectAllClients() {
    if (this.connectionList.length > 0) {
      this.connectionList.forEach((connection) => {
        if (connection.client && connection.client.connected) {
          console.log(`Disconnecting connection at ${connection.timestamp}`);
          connection.client.disconnect(() => {
            this.addConnectionDetail(SocketClientStateStream.DISCONNECTED, connection.client);
          });
        }
      });
      this.connectionList = [];
      this.activeClient = null;
    }
  }

  clearTopices() {
    this.subscribedTopics.clear();
  }

  public reSubscribeTopics() {
    if (this.topicResubscribers.size > 0) {
      console.log(`Resubscribing to ${this.topicResubscribers.size} topics...`);
      this.subscribedTopics.clear(); // Old client's subscriptions died with it
      this.topicResubscribers.forEach((resubscribe, topic) => {
        try {
          resubscribe(this.client);
          this.subscribedTopics.add(topic);
          console.log(`Resubscribed to topic: ${topic}`);
        } catch (e) {
          console.error(`Failed to resubscribe to topic: ${topic}`, e);
        }
      });
    } else {
      console.log('No topics to resubscribe to.');
    }
  }
  public subscribedTopicss(topic: string, handler = SocketServiceStream.jsonHandler) {
    if (this.subscribedTopics.has(topic)) {
      console.log(`Already subscribed to topic: ${topic}`);
      return;
    }

    const subscription: StompSubscription = this.client.subscribe(topic, message => {
      handler(message);
    });

    this.subscribedTopics.add(topic);
    console.log(`Subscribed to topic: ${topic}`);

    return () => {
      if (subscription.id) {
        this.client.unsubscribe(subscription.id);
        this.subscribedTopics.delete(topic);
        console.log(`Unsubscribed from topic: ${topic}`);
      }
    };
  }


  reloadconnection() {
     let status: any = {};
    status.connected = this.client.connected;
    status.streamTopic = this.state.observers.length;
    return status;
  }

  connect(): Observable<Client> {
    return new Observable<Client>(observer => {
      this.state.pipe(filter(state => state === SocketClientStateStream.CONNECTED)).subscribe(() => {
        observer.next(this.client);
      });
    });
  }
  ngOnDestroy() {
    this.disconnectClient();
    this.subscribedTopics.clear();
    this.topicResubscribers.clear();
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
    }
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    console.log('SocketService destroyed and cleaned up.');
  }


  onMessage(topic: string, handler = SocketServiceStream.jsonHandler): Observable<any> {
    return this.connect().pipe(
      first(),
      switchMap(client => new Observable<any>(observer => {
        if (!this.subscribedTopics.has(topic)) {
          const onFrame = (message: Message) => {
            observer.next(handler(message));
          };
          // `current` always points at the STOMP subscription on the client that is
          // connected right now; reSubscribeTopics() replaces it after a reconnect.
          let current: StompSubscription = client.subscribe(topic, onFrame);
          let currentClient: Client = client;
          this.topicResubscribers.set(topic, (nextClient: Client) => {
            current = nextClient.subscribe(topic, onFrame);
            currentClient = nextClient;
          });
          this.subscribedTopics.add(topic);
          console.log(`Subscribed to topic: ${topic}`);
          return () => {
            this.topicResubscribers.delete(topic);
            try {
              if (currentClient.connected) {
                currentClient.unsubscribe(current.id);
              }
            } catch (e) {
              // Client already gone; nothing to unsubscribe from.
            }
            this.subscribedTopics.delete(topic);
            console.log(`Unsubscribed from topic: ${topic}`);
          };
        } else {
          console.log(`Already subscribed to topic: ${topic}`);
          observer.complete();
        }
      }))
    );
  }

  onPlainMessage(topic: string): Observable<string> {
    return this.onMessage(topic, SocketServiceStream.textHandler);
  }

  send(topic: string, payload: any): void {
    this.connect()
      .pipe(first())
      .subscribe(inst => inst.send('/careonline' + topic, {}, JSON.stringify(payload)));
  }

  static jsonHandler(message: Message): any {
    return JSON.parse(message.body);
  }

  static textHandler(message: Message): string {
    return message.body;
  }

  getSubscribedTopics(): string[] {
    return Array.from(this.subscribedTopics);
  }

  private addConnectionDetail(state: SocketClientStateStream, client: Client) {
    this.connectionList.push({ timestamp: new Date(), state, client });
  }

  public disconnectAllClientsExceptLatest() {
    if (this.connectionList.length != 0) {
      console.log("stream", this.connectionList);
      const latestConnection = this.connectionList[this.connectionList.length - 1];
      this.connectionList.forEach((connection) => {
        console.log(connection);
        if (connection !== latestConnection && connection.client) {
          console.log(`Disconnecting connection at ${connection.timestamp}`);
          connection.client.disconnect(() => {
            this.addConnectionDetail(SocketClientStateStream.DISCONNECTED, connection.client);
          });
        }
      });
      this.connectionList = [latestConnection];
      this.activeClient = latestConnection.client || null;
      console.log('All other connections disconnected. Only the latest connection remains active.');
    }
  }

  getConnectionList(): ConnectionDetail[] {
    return this.connectionList;
  }

  getActiveClient(): Client | null {
    return this.activeClient;
  }


}

